/**
 * PendingReceiptsTable
 *
 * Tabla de recibos sin facturas aplicadas.
 * SIEMPRE visible en ListReceipt (con estado colapsable).
 * Retorna null solo en AddReceipt/ApplyReceipt cuando está vacía.
 *
 * Props:
 *   pendientes    : array de objetos recibo pendiente
 *   currentId     : id del recibo actual en ApplyReceipt (se resalta)
 *   pageSize      : cuántos mostrar por página (default 5)
 *   title         : texto del encabezado
 *   alwaysVisible : si true, nunca retorna null (para ListReceipt)
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function PendingReceiptsTable({
  pendientes = [],
  currentId = null,
  pageSize = 5,
  title,
  alwaysVisible = false,
  applyBasePath = "/finanzas/recibos",   // CxP usa "/finanzas/cxp/recibos"
  entityLabel,                            // encabezado columna entidad (default Filial)
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [collapsed, setCollapsed] = useState(false);

  // En modo normal (AddReceipt, ApplyReceipt): ocultar si no hay pendientes
  if (!alwaysVisible && pendientes.length === 0) return null;

  const hayPendientes = pendientes.length > 0;
  const totalPages = Math.max(1, Math.ceil(pendientes.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const items = pendientes.slice(start, start + pageSize);

  const defaultTitle = hayPendientes
    ? `${pendientes.length} ${t("recibos.sinFacturasAplicadas")}`
    : t("recibos.pendientesDeAplicar");

  const heading = title ?? defaultTitle;

  // Color del panel según estado
  const borderColor = hayPendientes ? "#f97316" : "var(--border-card)";
  const headingColor = hayPendientes ? "#f97316" : "var(--text-muted)";
  const iconClass = hayPendientes
    ? "fa-triangle-exclamation"
    : "fa-circle-check";
  const iconColor = hayPendientes ? "#f97316" : "#4ade80";

  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: `1px solid ${borderColor}`,
        borderRadius: 8,
        marginBottom: 16,
        overflow: "hidden",
        transition: "border-color 0.2s",
      }}
    >
      {/* ── Header (siempre visible, clickeable para colapsar) ─────── */}
      <div
        onClick={() => setCollapsed((c) => !c)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          borderBottom: collapsed ? "none" : `1px solid ${borderColor}`,
          gap: 8,
          cursor: "pointer",
          userSelect: "none",
          transition: "border-color 0.2s",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i
            className={`fa-solid ${iconClass}`}
            style={{ color: iconColor, fontSize: "0.9rem" }}
          />
          <span
            style={{
              color: headingColor,
              fontWeight: 600,
              fontSize: "0.88rem",
            }}
          >
            {heading}
          </span>

          {/* Badge con el número de pendientes */}
          {hayPendientes && (
            <span
              style={{
                background: "rgba(249,115,22,0.2)",
                border: "1px solid rgba(249,115,22,0.4)",
                color: "#f97316",
                borderRadius: 20,
                padding: "1px 8px",
                fontSize: "0.72rem",
                fontWeight: 700,
              }}
            >
              {pendientes.length}
            </span>
          )}

          {!hayPendientes && (
            <span
              style={{
                background: "rgba(74,222,128,0.1)",
                border: "1px solid rgba(74,222,128,0.25)",
                color: "#4ade80",
                borderRadius: 20,
                padding: "1px 8px",
                fontSize: "0.72rem",
                fontWeight: 700,
              }}
            >
              {t("recibos.todoDia")}
            </span>
          )}
        </span>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Paginación compacta — solo si está expandida y hay páginas */}
          {!collapsed && totalPages > 1 && hayPendientes && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexShrink: 0,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <MiniBtn
                disabled={safePage === 1}
                onClick={() => setPage((p) => p - 1)}
                label={t("common.anteriorCorto")}
              />
              <span
                style={{
                  color: "var(--text-muted)",
                  fontSize: "0.78rem",
                  whiteSpace: "nowrap",
                }}
              >
                {safePage} / {totalPages}
              </span>
              <MiniBtn
                disabled={safePage === totalPages}
                onClick={() => setPage((p) => p + 1)}
                label={t("common.siguienteCorto")}
              />
            </div>
          )}

          {/* Toggle collapse */}
          <i
            className={`fa-solid fa-chevron-${collapsed ? "down" : "up"}`}
            style={{
              color: "var(--text-muted)",
              fontSize: "0.75rem",
              transition: "transform 0.2s",
            }}
          />
        </div>
      </div>

      {/* ── Cuerpo (colapsable) ─────────────────────────────────────── */}
      {!collapsed &&
        (hayPendientes ? (
          <>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.84rem",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "var(--hover-bg)",
                      color: "var(--text-secondary)",
                    }}
                  >
                    <Th>{t("recibos.numRecibo")}</Th>
                    <Th>{entityLabel ?? t("common.filial")}</Th>
                    <Th>{t("common.monto")}</Th>
                    <Th style={{ color: "#f97316" }}>{t("recibos.sinAplicar")}</Th>
                    <Th>{t("common.fecha")}</Th>
                    <Th>{t("recibos.documento")}</Th>
                    <Th style={{ width: 110, textAlign: "center" }}>{t("common.acciones")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p, idx) => {
                    const isCurrent = p.id === currentId;
                    const esUSD = (p.moneda_pago || 'CRC').toUpperCase() === 'USD';
                    const sim   = esUSD ? '$' : '₡';
                    const locale = esUSD ? 'en-US' : 'en-US';
                    const fmtMonto = (n) =>
                      `${sim}${Number(n).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    return (
                      <tr
                        key={p.id}
                        style={{
                          borderBottom: "1px solid var(--border-card)",
                          background: isCurrent
                            ? "rgba(249,115,22,0.08)"
                            : idx % 2 === 0
                              ? "transparent"
                              : "var(--hover-bg)",
                        }}
                      >
                        <Td>
                          <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                            {p.numero_recibo}
                          </span>
                          {isCurrent && (
                            <span
                              style={{
                                marginLeft: 6,
                                fontSize: "0.7rem",
                                background: "rgba(249,115,22,0.2)",
                                color: "#fb923c",
                                padding: "1px 6px",
                                borderRadius: 4,
                                fontWeight: 600,
                              }}
                            >
                              {t("common.actual")}
                            </span>
                          )}
                        </Td>
                        <Td>
                          {(p.es_deposito_sin_identificar || p.es_pago_sin_identificar) &&
                           !(p.filial_id || p.proveedor_id)
                            ? <span style={{ background:'rgba(249,115,22,0.15)',
                                border:'1px solid rgba(249,115,22,0.4)', borderRadius:10,
                                padding:'2px 8px', color:'#f97316', fontSize:'0.72rem', fontWeight:700 }}>
                                {t("common.sinIdentificar")}
                              </span>
                            : (p.filial_label ?? p.proveedor_label)}
                        </Td>
                        <Td>{fmtMonto(p.monto)}</Td>
                        <Td style={{ color: "#f97316", fontWeight: 600 }}>
                          {fmtMonto(p.saldo_sin_aplicar || p.monto)}
                        </Td>
                        <Td>{p.fecha_pago}</Td>
                        <Td>{p.documento || <span style={{ color: "var(--text-muted)" }}>-</span>}</Td>
                        <Td style={{ textAlign: "center" }}>
                          {isCurrent ? (
                            <span
                              style={{ color: "var(--text-muted)", fontSize: "0.78rem" }}
                            >
                              {t("common.enEdicion")}
                            </span>
                          ) : (
                            <button
                              onClick={() =>
                                navigate(`${applyBasePath}/${p.id}/aplicar`)
                              }
                              style={{
                                padding: "5px 14px",
                                background: "#f97316",
                                color: "#fff",
                                border: "none",
                                borderRadius: 5,
                                fontSize: "0.78rem",
                                fontWeight: 600,
                                cursor: "pointer",
                                transition: "background 0.2s",
                                whiteSpace: "nowrap",
                              }}
                              onMouseOver={(e) =>
                                (e.currentTarget.style.background = "#c2570c")
                              }
                              onMouseOut={(e) =>
                                (e.currentTarget.style.background = "#f97316")
                              }
                            >
                              {t("recibos.aplicar")}
                            </button>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div
              style={{
                padding: "6px 16px",
                borderTop: "1px solid var(--border-card)",
                color: "var(--text-muted)",
                fontSize: "0.78rem",
                textAlign: "right",
              }}
            >
              {t("common.mostrando")} {start + 1}-
              {Math.min(start + pageSize, pendientes.length)} {t("common.de")}{" "}
              {pendientes.length} {t("recibos.titulo").toLowerCase()}
            </div>
          </>
        ) : (
          /* Vacío: mensaje amigable */
          <div
            style={{
              padding: "16px",
              textAlign: "center",
              color: "var(--text-muted)",
              fontSize: "0.85rem",
            }}
          >
            <i
              className="fa-solid fa-circle-check"
              style={{ color: "#4ade80", marginRight: 8, fontSize: "1.1rem" }}
            />
            {t("recibos.todosAplicados")}
          </div>
        ))}
    </div>
  );
}

function Th({ children, style = {} }) {
  return (
    <th
      style={{
        padding: "8px 12px",
        textAlign: "left",
        fontWeight: 600,
        letterSpacing: "0.3px",
        fontSize: "0.78rem",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, style = {} }) {
  return (
    <td
      style={{
        padding: "9px 12px",
        color: "var(--text-secondary)",
        verticalAlign: "middle",
        ...style,
      }}
    >
      {children}
    </td>
  );
}

function MiniBtn({ disabled, onClick, label }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        padding: "3px 8px",
        background: disabled ? "transparent" : "var(--hover-bg)",
        border: "1px solid var(--border-card)",
        borderRadius: 4,
        color: disabled ? "var(--text-muted)" : "var(--text-secondary)",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: "0.76rem",
        whiteSpace: "nowrap",
        transition: "background 0.15s",
      }}
    >
      {label}
    </button>
  );
}
